import React from 'react';
import { Button } from '@/components/ui/button';
import { Lock, AlertTriangle, Inbox } from 'lucide-react';

/**
 * PermissionAwareState — renders exactly ONE of three distinct states with
 * clear, non-conflating wording. Preserves the original HTTP status for
 * caller-side logging (never suppresses it).
 *
 *   1. EMPTY DATA STATE       — authorised request succeeded with zero records.
 *   2. PERMISSION-DENIED STATE — backend returned 401 or 403.
 *   3. ERROR STATE            — network failure, server error, malformed response.
 *
 * Boundaries:
 *   - MUST NOT convert every backend failure into a permission message.
 *   - MUST NOT convert a 403 into an empty successful response.
 *   - MUST NOT expose sensitive backend error detail.
 *   - MUST preserve the original HTTP status internally (caller logs to console).
 *
 * Props:
 *   status     — 'empty' | 'denied' | 'error'
 *   emptyText  — optional override for the empty-data wording
 *   onRetry    — optional callback; renders a Retry button in the error state
 *   testIdBase — prefix for data-testid values (e.g. 'appointments')
 */
export function PermissionAwareState({ status, emptyText, onRetry, testIdBase }) {
  if (status === 'empty') {
    return (
      <div
        className="p-6 text-sm text-muted-foreground text-center flex flex-col items-center gap-2"
        role="status"
        aria-live="polite"
        data-testid={`${testIdBase}-state-empty`}
      >
        <Inbox className="w-5 h-5 text-slate-400" aria-hidden="true" />
        <p>{emptyText || 'No records found for this participant.'}</p>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div
        className="p-6 text-sm text-muted-foreground text-center flex flex-col items-center gap-2"
        role="status"
        aria-live="polite"
        data-testid={`${testIdBase}-state-denied`}
      >
        <Lock className="w-5 h-5 text-amber-500" aria-hidden="true" />
        <p>
          You do not have permission to view this information.
          <br className="hidden sm:block" />
          Contact an administrator if you believe you need access.
        </p>
      </div>
    );
  }

  // error
  return (
    <div
      className="p-6 text-sm text-red-600 text-center flex flex-col items-center gap-2"
      role="alert"
      aria-live="assertive"
      data-testid={`${testIdBase}-state-error`}
    >
      <AlertTriangle className="w-5 h-5 text-red-500" aria-hidden="true" />
      <p>
        This information could not be loaded.
        <br className="hidden sm:block" />
        Try again or contact support if the problem continues.
      </p>
      {onRetry && (
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          data-testid={`${testIdBase}-state-error-retry`}
        >
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * classifyFetchError — maps an axios/fetch error into the three canonical
 * states. Preserves the original HTTP status via the return object so callers
 * can log it (does NOT suppress).
 *
 * Returns: { state: 'empty' | 'denied' | 'error', httpStatus: number | null }
 */
export function classifyFetchError(err) {
  const httpStatus = err?.response?.status ?? null;
  if (httpStatus === 401 || httpStatus === 403) {
    return { state: 'denied', httpStatus };
  }
  return { state: 'error', httpStatus };
}

export default PermissionAwareState;
